import React from 'react';
import {
  Activity,
  Clock,
  Eye,
  Gauge,
  Settings,
  Target,
  ThumbsDown,
  ThumbsUp,
  User,
  Zap,
} from 'lucide-react';
import { Button } from '../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { cn } from '../../../../../../../lib/utils';

type ZoneLikeStatus = 'TRUE' | 'FALSE' | 'EX' | 'NULL';

export type WeldingZoneSampleRow = {
  id: string;
  stationNumber: number | null;
  welderId: string;
  bugType: string;
  torch: string;
  passLabel: string;
  zoneLabel: string;
  weldStartTime: string;
  weldTime: string;
  autoincrement: string;
  status: ZoneLikeStatus;
  current: { min: string; avg: string; max: string };
  voltage: { min: string; avg: string; max: string };
  travelSpeed: { min: string; avg: string; max: string };
};

const ZONE_ROWS: WeldingZoneSampleRow[] = [
  {
    id: 'z1',
    stationNumber: 1,
    welderId: 'W-1042',
    bugType: 'CW',
    torch: 'T1',
    passLabel: 'Pass 1',
    zoneLabel: 'Zone 1',
    weldStartTime: '08:14:18',
    weldTime: '1m 04s',
    autoincrement: 'Z-91021',
    status: 'TRUE',
    current: { min: '158', avg: '163', max: '169' },
    voltage: { min: '24.0', avg: '24.9', max: '25.8' },
    travelSpeed: { min: '17.4', avg: '18.0', max: '18.8' },
  },
  {
    id: 'z2',
    stationNumber: 1,
    welderId: 'W-1042',
    bugType: 'CCW',
    torch: 'T1',
    passLabel: 'Pass 2',
    zoneLabel: 'Zone 2',
    weldStartTime: '08:15:28',
    weldTime: '1m 12s',
    autoincrement: 'Z-91022',
    status: 'TRUE',
    current: { min: '170', avg: '176', max: '181' },
    voltage: { min: '25.1', avg: '25.8', max: '26.4' },
    travelSpeed: { min: '16.9', avg: '17.6', max: '18.2' },
  },
  {
    id: 'z3',
    stationNumber: 2,
    welderId: 'W-1042',
    bugType: 'CCW',
    torch: 'T2',
    passLabel: 'Pass 2',
    zoneLabel: 'Zone 3',
    weldStartTime: '08:32:01',
    weldTime: '0m 48s',
    autoincrement: 'Z-91023',
    status: 'EX',
    current: { min: '168', avg: '174', max: '182' },
    voltage: { min: '25.0', avg: '25.6', max: '26.3' },
    travelSpeed: { min: '13.5', avg: '14.2', max: '15.1' },
  },
  {
    id: 'z4',
    stationNumber: 2,
    welderId: 'W-1042',
    bugType: 'CCW',
    torch: 'T2',
    passLabel: 'Pass 3',
    zoneLabel: 'Zone 4',
    weldStartTime: '08:41:10',
    weldTime: '0m 52s',
    autoincrement: 'Z-91024',
    status: 'FALSE',
    current: { min: '184', avg: '190', max: '196' },
    voltage: { min: '26.5', avg: '27.6', max: '28.2' },
    travelSpeed: { min: '12.1', avg: '12.9', max: '13.5' },
  },
];

function StatusCell({ status }: { status: ZoneLikeStatus }) {
  if (status === 'TRUE') return <ThumbsUp className="h-5 w-5 text-green-600" />;
  if (status === 'FALSE') return <ThumbsDown className="h-5 w-5 text-red-600" />;
  if (status === 'EX') {
    return (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-yellow-400 text-xs font-bold text-black">
        !
      </span>
    );
  }
  return <span className="text-gray-600">-</span>;
}

function ParamHeader({
  icon: Icon,
  title,
  unit,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  unit: string;
}) {
  return (
    <th className="min-w-[130px] max-w-[200px] border-l border-gray-200 px-2">
      <div className="flex h-[72px] flex-col items-center justify-center py-1">
        <div className="flex items-center gap-1 text-center text-xs font-medium text-gray-600">
          <Icon className="h-4 w-4 shrink-0" />
          <span>
            {title}
            <br />
            <span className="font-normal text-gray-500">({unit})</span>
          </span>
        </div>
        <div className="mt-1 flex w-full justify-around border-t border-gray-100 pt-1 text-[10px] font-medium text-gray-500">
          <span>Min</span>
          <span className="text-gray-700">Avg</span>
          <span>Max</span>
        </div>
      </div>
    </th>
  );
}

function ParamCell({ min, avg, max }: { min: string; avg: string; max: string }) {
  return (
    <td className="h-[52px] min-w-0 border-l border-gray-200 px-2 py-2 text-xs">
      <div className="flex justify-around gap-1 font-mono">
        <span className="text-gray-500">{min}</span>
        <span className="font-semibold text-gray-900">{avg}</span>
        <span className="text-gray-500">{max}</span>
      </div>
    </td>
  );
}

const STICKY = {
  sl: 0,
  status: 72,
  station: 132,
  actions: 132 + 128,
} as const;

export interface WeldingZoneSampleTableProps {
  titleHint?: string;
  embedded?: boolean;
  className?: string;
}

/** Zone view sample table — sticky columns + Pass / Zone + min·avg·max parameters (aligned with {@link ZoneViewTable}). */
export function WeldingZoneSampleTable({ titleHint, embedded, className }: WeldingZoneSampleTableProps) {
  const scroll = (
        <div
          className={
            embedded
              ? 'max-h-[min(420px,50vh)] overflow-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100'
              : 'max-h-[min(260px,38vh)] overflow-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100'
          }
        >
          <table className="w-full border-collapse" style={{ minWidth: 'max-content' }}>
            <thead className="sticky top-0 z-30 bg-gray-50 shadow-sm">
              <tr className="h-[72px] border-b bg-gray-50">
                <th
                  className="sticky left-0 z-20 min-w-[72px] bg-gray-50 px-2 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] sm:px-4"
                  style={{ left: STICKY.sl }}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Sl.no</span>
                    <span className="sm:hidden">#</span>
                  </div>
                </th>
                <th
                  className="sticky z-20 w-[60px] min-w-[60px] max-w-[60px] bg-gray-50 px-2 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] sm:px-4"
                  style={{ left: STICKY.status }}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
                    Status
                  </div>
                </th>
                <th
                  className="sticky z-20 min-w-[100px] bg-gray-50 px-2 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] sm:px-4"
                  style={{ left: STICKY.station }}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                    Station
                  </div>
                </th>
                <th
                  className="sticky z-20 min-w-[72px] bg-gray-50 px-2 text-center text-xs font-medium text-gray-600 sm:min-w-[80px] sm:px-4"
                  style={{ left: STICKY.actions, boxShadow: '4px 0 6px -2px rgba(0,0,0,0.12)' }}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Actions</span>
                  </div>
                </th>

                <th className="min-w-[100px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <User className="h-3 w-3 sm:h-4 sm:w-4" />
                    Welder ID
                  </div>
                </th>
                <th className="min-w-[100px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                    Bug Type
                  </div>
                </th>
                <th className="min-w-[80px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <User className="h-3 w-3 sm:h-4 sm:w-4" />
                    Torch
                  </div>
                </th>
                <th className="min-w-[100px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                    Pass
                  </div>
                </th>
                <th className="min-w-[100px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                    Zone
                  </div>
                </th>
                <th className="min-w-[120px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Weld Start</span>
                    <span className="sm:hidden">Start</span>
                  </div>
                </th>
                <th className="min-w-[72px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <Gauge className="h-3 w-3 sm:h-4 sm:w-4" />
                    Weld Time
                  </div>
                </th>
                <th className="min-w-[100px] border-r border-gray-200 px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                    Auto Inc
                  </div>
                </th>

                <ParamHeader icon={Zap} title="Current" unit="A" />
                <ParamHeader icon={Zap} title="Voltage" unit="V" />
                <ParamHeader icon={Gauge} title="Travel Speed" unit="cm/min" />
              </tr>
            </thead>
            <tbody>
              {ZONE_ROWS.map((row, index) => (
                <tr
                  key={row.id}
                  className={`h-[52px] ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${
                    index < ZONE_ROWS.length - 1 ? 'border-b' : ''
                  }`}
                >
                  <td
                    className={`sticky z-10 px-2 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] sm:px-4 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                    style={{ left: STICKY.sl }}
                  >
                    {index + 1}
                  </td>
                  <td
                    className={`sticky z-10 w-[60px] min-w-[60px] max-w-[60px] px-2 text-center shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] sm:px-4 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                    style={{ left: STICKY.status }}
                  >
                    <div className="inline-flex justify-center">
                      <StatusCell status={row.status} />
                    </div>
                  </td>
                  <td
                    className={`sticky z-10 min-w-[100px] px-2 text-center text-xs text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] sm:px-4 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                    style={{ left: STICKY.station }}
                  >
                    {row.stationNumber != null ? `Station ${row.stationNumber}` : '-'}
                  </td>
                  <td
                    className={`sticky z-10 px-2 text-center sm:px-4 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                    style={{ left: STICKY.actions, boxShadow: '4px 0 6px -2px rgba(0,0,0,0.12)' }}
                  >
                    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-gray-100" aria-label="View zone">
                      <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </td>

                  <td className="px-2 text-center text-xs text-gray-600 sm:px-4">{row.welderId}</td>
                  <td className="px-2 text-center text-xs text-gray-600 sm:px-4">{row.bugType}</td>
                  <td className="px-2 text-center text-xs text-gray-600 sm:px-4">{row.torch}</td>
                  <td className="px-2 text-center text-xs text-gray-700 sm:px-4">{row.passLabel}</td>
                  <td className="px-2 text-center text-xs font-medium text-gray-800 sm:px-4">{row.zoneLabel}</td>
                  <td className="px-2 text-center text-xs text-gray-600 sm:px-4">{row.weldStartTime}</td>
                  <td className="px-2 text-center text-xs text-gray-600 sm:px-4">{row.weldTime}</td>
                  <td className="border-r border-gray-200 px-2 text-center text-xs text-gray-600 sm:px-4">
                    {row.autoincrement}
                  </td>

                  <ParamCell min={row.current.min} avg={row.current.avg} max={row.current.max} />
                  <ParamCell min={row.voltage.min} avg={row.voltage.avg} max={row.voltage.max} />
                  <ParamCell min={row.travelSpeed.min} avg={row.travelSpeed.avg} max={row.travelSpeed.max} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
  );

  if (embedded) {
    return <div className={cn('overflow-hidden rounded-md border border-gray-200 bg-white', className)}>{scroll}</div>;
  }

  return (
    <Card className="border border-gray-200">
      <div className="flex flex-col gap-0.5 border-b bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Zone view</h3>
        {titleHint ? <span className="text-xs font-medium text-gray-500">{titleHint}</span> : null}
      </div>
      <CardContent className="p-0">{scroll}</CardContent>
    </Card>
  );
}
